/**
 * HardwareLibrary.js
 * Definitions for external hardware synthesizers and drum machines.
 * Maps specific MIDI CCs for performance controls.
 */

export const HARDWARE_LIBRARY = [
    // --- 1. POLYPHONIC SYNTHS ---
    {
        id: 'prophet_6',
        name: 'Sequential Prophet-6',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 6,
        roles: ['pads', 'keys', 'lead'],
        controls: [
            { label: 'Cutoff', cc: 102, min: 0, max: 127, default: 64 }, // Verified (Low Pass Filter)
            { label: 'Resonance', cc: 104, min: 0, max: 127, default: 0 }, // Verified
            { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'ob_6',
        name: 'Sequential OB-6',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 6,
        roles: ['pads', 'texture', 'keys'],
        controls: [
            { label: 'Cutoff', cc: 74, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 71, min: 0, max: 127, default: 0 },
            { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'ju_06a',
        name: 'Roland JU-06A',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 4,
        roles: ['pads', 'bass', 'keys'],
        controls: [
            { label: 'Filter', cc: 74, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 71, min: 0, max: 127, default: 0 },
            { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'minilogue_xd',
        name: 'Korg Minilogue XD',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 4,
        roles: ['pads', 'lead', 'arp'],
        controls: [
            { label: 'Cutoff', cc: 43, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 44, min: 0, max: 127, default: 0 },
            { label: 'EG Int', cc: 22, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 19, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'polybrute',
        name: 'Arturia PolyBrute',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 6,
        roles: ['texture', 'pads', 'lead'],
        controls: [
             { label: 'Cutoff', cc: 74, min: 0, max: 127, default: 64 },
             { label: 'Resonance', cc: 71, min: 0, max: 127, default: 0 },
             { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
             { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'digitone',
        name: 'Elektron Digitone',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 8,
        roles: ['pads', 'keys', 'percussion'],
        controls: [
            { label: 'Filter Freq', cc: 23, min: 0, max: 127, default: 64 }, // Verified
            { label: 'Resonance', cc: 24, min: 0, max: 127, default: 0 }, // Verified
            { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    // Keep existing Novation Peak as it doesn't conflict and is useful
    {
        id: 'novation_peak',
        name: 'Novation Peak',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 8,
        roles: ['pads', 'keys'],
        controls: [
            { label: 'Cutoff', cc: 29, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 79, min: 0, max: 127, default: 0 },
            { label: 'Drive', cc: 80, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 89, min: 0, max: 127, default: 40 }
        ]
    },
    // Keep Hydrasynth
     {
        id: 'hydrasynth',
        name: 'ASM Hydrasynth',
        type: 'hardware_poly',
        midiChannel: 1,
        polyphony: 8,
        roles: ['pads', 'atmosphere', 'lead'],
        controls: [
            { label: 'Macro 1', cc: 70, min: 0, max: 127, default: 0 },
            { label: 'Macro 2', cc: 71, min: 0, max: 127, default: 0 },
            { label: 'Macro 3', cc: 72, min: 0, max: 127, default: 0 },
            { label: 'Macro 4', cc: 73, min: 0, max: 127, default: 0 }
        ]
    },


    // --- 2. MONOPHONIC SYNTHS ---
    {
        id: 'subsequent_37',
        name: 'Moog Subsequent 37',
        type: 'hardware_mono',
        midiChannel: 1,
        polyphony: 1,
        roles: ['bass', 'lead'],
        controls: [
            { label: 'Cutoff', cc: 19, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 21, min: 0, max: 127, default: 0 },
            { label: 'Drive', cc: 18, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 26, min: 0, max: 127, default: 20 }
        ]
    },
    {
        id: 'sh_01a',
        name: 'Roland SH-01A',
        type: 'hardware_mono',
        midiChannel: 1,
        polyphony: 1,
        roles: ['bass', 'lead', 'arp'],
        controls: [
            { label: 'Cutoff', cc: 74, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 71, min: 0, max: 127, default: 0 },
            { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'bass_station_ii',
        name: 'Novation Bass Station II',
        type: 'hardware_mono',
        midiChannel: 1,
        polyphony: 1,
        roles: ['bass', 'lead'],
        controls: [
            { label: 'Cutoff', cc: 16, min: 0, max: 127, default: 64 }, // Verified
            { label: 'Resonance', cc: 82, min: 0, max: 127, default: 0 }, // Verified
            { label: 'Distortion', cc: 94, min: 0, max: 127, default: 0 },
            { label: 'Osc Filter Mod', cc: 115, min: 0, max: 127, default: 0 }
        ]
    },
    {
        id: 'minitaur',
        name: 'Moog Minitaur',
        type: 'hardware_mono',
        midiChannel: 1,
        polyphony: 1,
        roles: ['bass'],
        controls: [
            { label: 'Cutoff', cc: 19, min: 0, max: 127, default: 64 }, // Verified
            { label: 'Resonance', cc: 21, min: 0, max: 127, default: 0 }, // Verified
            { label: 'Attack', cc: 73, min: 0, max: 127, default: 0 },
            { label: 'Release', cc: 72, min: 0, max: 127, default: 40 }
        ]
    },
    {
        id: 'microfreak',
        name: 'Arturia MicroFreak',
        type: 'hardware_mono', // Technically paraphonic but treated as mono source often
        midiChannel: 1,
        polyphony: 4, // Updated to 4 per request (Paraphonic)
        roles: ['lead', 'arp', 'texture'],
        controls: [
            { label: 'Cutoff', cc: 23, min: 0, max: 127, default: 64 },
            { label: 'Resonance', cc: 83, min: 0, max: 127, default: 0 },
            { label: 'Type', cc: 9, min: 0, max: 127, default: 0 },
            { label: 'Wave', cc: 10, min: 0, max: 127, default: 64 }
        ]
    },
    // Keep Moog Grandmother
    {
        id: 'moog_grandmother',
        name: 'Moog Grandmother',
        type: 'hardware_mono',
        midiChannel: 1,
        polyphony: 1,
        roles: ['bass', 'lead'],
        controls: [
            { label: 'Mod Wheel (Cutoff)', cc: 1, min: 0, max: 127, default: 0 },
            { label: 'Glide', cc: 5, min: 0, max: 127, default: 0 }
        ]
    },


    // --- 3. DRUM MACHINES ---
    // Preserving existing drum machines
    {
        id: 'roland_tr8s',
        name: 'Roland TR-8S',
        type: 'hardware_drum',
        midiChannel: 10,
        polyphony: 11,
        noteMap: 'gm',
        roles: ['drums', 'percussion'],
        controls: [
            { label: 'BD Tune', cc: 20, min: 0, max: 127, default: 64 },
            { label: 'BD Decay', cc: 23, min: 0, max: 127, default: 64 },
            { label: 'SD Snappy', cc: 25, min: 0, max: 127, default: 64 },
            { label: 'Kit Level', cc: 7, min: 0, max: 127, default: 100 }
        ]
    },
    {
        id: 'elektron_digitakt',
        name: 'Elektron Digitakt',
        type: 'hardware_drum',
        midiChannel: 10,
        polyphony: 8,
        noteMap: 'chromatic_c1',
        roles: ['drums', 'sampler'],
        controls: [
            { label: 'Filter Freq', cc: 74, min: 0, max: 127, default: 127 },
            { label: 'Resonance', cc: 75, min: 0, max: 127, default: 0 },
            { label: 'Reverb Send', cc: 16, min: 0, max: 127, default: 0 },
            { label: 'Delay Send', cc: 17, min: 0, max: 127, default: 0 }
        ]
    },
    {
        id: 'behringer_rd8',
        name: 'Behringer RD-8',
        type: 'hardware_drum',
        midiChannel: 10,
        polyphony: 11,
        noteMap: 'gm',
        roles: ['drums'],
        controls: [
            { label: 'Filter Cutoff', cc: 20, min: 0, max: 127, default: 127 },
            { label: 'Filter Res', cc: 21, min: 0, max: 127, default: 0 },
        ]
    }
];

export const getHardwareById = (id) => HARDWARE_LIBRARY.find(h => h.id === id);
